import { useQuery } from '@apollo/client'
import { FetchViewSyncTimeDocument, type ViewFieldsFragment } from '@salescore/client-api'
import { useClearViewQueryCache, useMe } from '@salescore/client-common'
import {
  selectCurrentCachedView,
  selectShouldUseRefetchCache,
  useAppSelector,
  useUpsertCachedView,
} from '@salescore/client-redux'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryField,
  ViewQueryList,
} from '@salescore/core'
import { useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { useMustacheParameter } from '../../hooks/useMustacheParameter'
import { usePageSizeState } from '../../hooks/usePageSizeState'
import { kpiPivotPickedPresetNameAtom, sheetPickedPresetNameAtom } from '../navigation/atoms'
import { additionalConfigAtom } from '../records/atoms'
import { useRefetchAggregationQueryResult } from '../records/mutations/useRefetchAggregationQueryResult'
import { useRefetchKpiPivotQueryResult } from '../records/mutations/useRefetchKpiPivotQueryResult'
import { useRefetchMutation } from '../records/mutations/useRefetchMutation'
import { changesAtom } from '../recordsHistory'
import {
  kpiFormAtom,
  kpiFormModifiedAtom,
  type SalescoreObjectAbility,
  type ViewAbility,
  viewsContextAtom,
} from '../view/atoms'
import { useWatchView } from '../view/hooks'
import {
  configAtom,
  resolveShouldJoinRecordsInApplication,
  useAggregationQueries,
  useMultiTablePivotQuery,
  useViewConfigSheet,
} from '../viewConfig'

/**
 * ViewComponent の Body で呼ばれる監視ロジックの集約 hook。
 *
 * 主な役割は `query` が書き換わったときに `refetch` を実行すること (= ユーザーが config を
 * 書き換えるとここからレコードが再取得される)。
 *
 * 2023/11 以前は「config が書き換わったら `onSaveView` で自動保存」のロジックもありましたが、
 * 自動保存自体がなくなったため、この種のロジックは消えています (フック自体は残存)。
 */
// eslint-disable-next-line sonarjs/todo-tag
// TODO: 命名が思いつかない
export const useWatch = ({
  onSaveView,
  compiledConfig,
  view,
  ability,
  query,
  hasChangeToKpiPivotParameter,
  hasChangeToViewSchema,
  setHasChangeToViewSchema,
}: {
  onSaveView?: (views: ViewFieldsFragment[]) => void
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  query: ViewQueryList | undefined
  hasChangeToKpiPivotParameter: boolean
  hasChangeToViewSchema: boolean
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): void => {
  const { clearTargetViewQueryCaches } = useClearViewQueryCache()
  const me = useMe()
  const configSheet = useViewConfigSheet()
  const shouldJoinRecordsInApplication = resolveShouldJoinRecordsInApplication(me, configSheet)
  useWatchView({ onSaveView, compiledConfig, view, query, hasChangeToViewSchema, setHasChangeToViewSchema })
  const aggregationQueries = useAggregationQueries(compiledConfig)
  const multiTablePivotQuery = useMultiTablePivotQuery(compiledConfig)
  const mustacheParameter = useMustacheParameter()
  const changes = useRecoilValue(changesAtom)
  const config = useRecoilValue(configAtom)
  const [pageSize] = usePageSizeState()
  const [previousQuery, setPreviousQuery] = useState<ViewQueryList | undefined>()
  const [previousViewId, setPreviousViewId] = useState<string | undefined>()
  const [previousKpiPivotPresetName, setPreviousKpiPivotPresetName] = useState<string | undefined>()
  const [previousSheetPresetName, setPreviousSheetPresetName] = useState<string | undefined>()

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { refetch } = useRefetchMutation(view, query)
  const refetchAggregationQueryResult = useRefetchAggregationQueryResult(compiledConfig, view)
  const refetchKpiPivotQueryResult = useRefetchKpiPivotQueryResult(compiledConfig, view)
  const { shouldInitializeWithNewRecord } = useRecoilValue(viewsContextAtom)
  const currentCachedView = useAppSelector((state) => selectCurrentCachedView(state, { viewId: view.id }))
  const { data: oldestSyncedAtData } = useQuery(FetchViewSyncTimeDocument, {
    variables: {
      organizationId: me.organization.id,
      viewId: view.id,
    },
  })
  const oldestSyncedAt = oldestSyncedAtData?.viewOldestSyncTime ?? undefined
  const shouldUseRefetchCache = useAppSelector((state) =>
    selectShouldUseRefetchCache(state, {
      viewId: view.id,
      oldestSyncedAt,
    }),
  )
  const additionalConfig = useRecoilValue(additionalConfigAtom)
  const [kpiPivotPickedPresetName] = useRecoilState(kpiPivotPickedPresetNameAtom)
  const [sheetPickedPresetName] = useRecoilState(sheetPickedPresetNameAtom)
  const form = useRecoilValue(kpiFormAtom)
  const formModified = useRecoilValue(kpiFormModifiedAtom)
  const upsertCachedView = useUpsertCachedView()

  function pathAndName(field: ViewQueryField) {
    return `${field.nodePath.join('.')}.${field.name}`
  }

  function fieldsReordered(fields1: ViewQueryField[], fields2: ViewQueryField[]) {
    const pathAndNameSet2 = new Set(fields2.map((element) => pathAndName(element)))
    if (fields1.length === fields2.length && fields1.every((x) => pathAndNameSet2.has(pathAndName(x)))) {
      // フィールドの順番が変更されたかどうかの判定
      for (const [index, element] of fields1.entries()) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (pathAndName(element) !== pathAndName(fields2[index]!)) {
          // fields1 と fields2 は同じ長さであることが保証されている
          return true
        }
      }
    }
    return false
  }

  function resetIgnoreCacheFlag(): void {
    if (currentCachedView?.shouldIgnoreRefetchCache === true) {
      upsertCachedView({
        viewId: currentCachedView.viewId,
        shouldIgnoreRefetchCache: false,
      })
    }
  }

  useEffect(() => {
    upsertCachedView({
      viewId: view.id,
      config,
      changes,
      kpiForm: form,
      additionalConfig,
      kpiPivotPickedPresetName,
      sheetPickedPresetName,
      oldestSyncedAt,
      isModifiedKpiForm: formModified,
      hasChangeToViewSchema,
      hasChangeToKpiPivotParameter,
    })
  }, [
    JSON.stringify({
      view,
      config,
      changes,
      form,
      additionalConfig,
      kpiPivotPickedPresetName,
      sheetPickedPresetName,
      oldestSyncedAt,
      formModified,
      hasChangeToViewSchema,
      hasChangeToKpiPivotParameter,
    }),
  ])

  // カーソル以外の条件が書き換わった場合の初期化処理
  // viewの変更とqueryの変更を別々のhooksで行なったりすると、新しいviewId・古いqueryの状態でfetchが走ったりするので注意

  useEffect(() => {
    // eslint-disable-next-line sonarjs/todo-tag
    // 初期化されるまでqueryはundefinedになっている // TODO: これどう防ぐのが良いか？
    if (query === undefined) {
      return
    }
    if (shouldInitializeWithNewRecord === true) {
      return
    }

    if (previousViewId === view.id && previousQuery !== undefined) {
      // ビューIDは変更ないが、queryが変更された場合
      if (query.fields.length > previousQuery.fields.length) {
        const previousQueryFieldsPaths = new Set(previousQuery.fields.map((element) => pathAndName(element)))
        const addedFields = query.fields.filter((x) => !previousQueryFieldsPaths.has(pathAndName(x)))
        void refetch({ kind: 'addColumn', fields: addedFields })
      } else if (query.fields.length < previousQuery.fields.length) {
        const queryFieldsPaths = new Set(query.fields.map((element) => pathAndName(element)))
        const deletedFields = previousQuery.fields.filter((x) => !queryFieldsPaths.has(pathAndName(x)))
        void refetch({ kind: 'deleteColumn', fields: deletedFields })
      } else if (
        fieldsReordered(query.fields, previousQuery.fields) &&
        ((config.type === 'sheet' && sheetPickedPresetName === previousSheetPresetName) ||
          (config.type === 'kpiPivot' && kpiPivotPickedPresetName === previousKpiPivotPresetName))
      ) {
        // プリセットが同一かつ列の並び替えの場合は Refetch する必要はない
      } else {
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: 列の追加・削除以外の条件が変更された場合の細かな処理
        void refetch({ kind: 'reset' })
      }
    } else {
      void refetch({ kind: 'changeView' }, shouldUseRefetchCache)
      resetIgnoreCacheFlag()
      if (!shouldUseRefetchCache) {
        clearTargetViewQueryCaches({ viewIds: [view.id] })
      }
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPreviousQuery(query)
    setPreviousViewId(view.id)
    setPreviousKpiPivotPresetName(kpiPivotPickedPresetName)
    setPreviousSheetPresetName(sheetPickedPresetName)
  }, [view.id, JSON.stringify(query), JSON.stringify(mustacheParameter), pageSize, shouldJoinRecordsInApplication])

  useEffect(() => {
    void refetchAggregationQueryResult.refetch()
    resetIgnoreCacheFlag()
    if (!shouldUseRefetchCache) {
      clearTargetViewQueryCaches({ viewIds: [view.id] })
    }
  }, [view.id, JSON.stringify(aggregationQueries), mustacheParameter])

  useEffect(() => {
    refetchKpiPivotQueryResult.refetch(shouldUseRefetchCache)
    resetIgnoreCacheFlag()
    if (!shouldUseRefetchCache) {
      clearTargetViewQueryCaches({ viewIds: [view.id] })
    }
  }, [view.id, JSON.stringify(multiTablePivotQuery), mustacheParameter])
}
